import React from 'react';
import { Alert, Badge, Button, Card, CardBody, CardHeader } from 'reactstrap';

import { formatAmount, formatDay } from '../../utils';
import Loading from '../Loading';
import Pagination from '../Pagination';
import {
  listPaymentNotes,
  periodLabel,
} from '../../services/paymentNote.service';
import DataTable from '../DataTable';

const LIMIT = 30;

// The payment notes already issued against this wallet.
//
// The one thing to get right here: each row's `amount_due` is a FROZEN snapshot
// of what was requested when that note was issued, and it never changes. There
// is only ever one overdraft balance, so the rows do not add up to a debt — a
// customer billed ₦45,500 in July and ₦20,000 in August does not owe ₦65,500.
//
// The live figure is `meta.outstanding_now`, taken from the wallet, and it is
// the only aggregate shown anywhere on this screen. Nothing here sums a column.
class PaymentNoteHistory extends React.Component {
  state = {
    loading: false,
    loaded: false,
    error: null,
    rows: [],
    meta: { total: 0, pages: 1, outstanding_now: 0 },
    page: 1,
  };

  componentDidMount() {
    this.load();
  }

  // `outstanding_now` is read off the live wallet, so a wallet write or a newly
  // issued note both change what this table should say.
  componentDidUpdate(prevProps) {
    if (prevProps.refreshToken !== this.props.refreshToken) {
      this.setState({ page: 1 }, this.load);
    }
  }

  load = () => {
    this.setState({ loading: true, error: null });

    listPaymentNotes(this.props.userId, {
      page: this.state.page,
      limit: LIMIT,
    })
      .then(({ rows, meta }) => {
        this.setState({
          loading: false,
          loaded: true,
          rows,
          meta: { total: 0, pages: 1, outstanding_now: 0, ...meta },
        });
      })
      .catch((err) => {
        this.setState({
          loading: false,
          loaded: true,
          error:
            (err && err.message) || 'Unable to load the payment note history.',
        });
      });
  };

  setPage = (page) => {
    if (!page) return;
    this.setState({ page }, this.load);
  };

  statusBadge(row) {
    if (row.settled) return <Badge color="success">Settled</Badge>;
    if (row.overdue) return <Badge color="danger">Overdue</Badge>;
    if (row.status === 'issued') return <Badge color="info">Issued</Badge>;
    return <Badge color="secondary">{row.status || 'Unknown'}</Badge>;
  }

  renderBody() {
    const { loading, loaded, error, rows } = this.state;

    if (error) {
      return (
        <div className="text-center" style={{ padding: '20px 0' }}>
          <p className="text-muted">{error}</p>
          <Button color="primary" size="sm" onClick={this.load}>
            Retry
          </Button>
        </div>
      );
    }

    if (loading || !loaded) return <Loading />;

    if (!rows.length) {
      return (
        <div className="text-center text-muted" style={{ padding: '20px 0' }}>
          No payment note has been issued to this customer.
        </div>
      );
    }

    return (
      <DataTable card={false} loading={loading} minWidth={800}>
        <thead className="text-primary">
          <tr>
            <th>Reference</th>
            <th>Period</th>
            <th>Issued</th>
            <th>Due</th>
            <th className="text-right">Amount requested</th>
            <th>Status</th>
            <th>Issued by</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className={row.overdue ? 'table-danger' : ''}>
              <td style={{ whiteSpace: 'nowrap' }}>{row.reference || '—'}</td>
              <td>{periodLabel(row.period)}</td>
              <td>{row.issued_at ? formatDay(row.issued_at) : '—'}</td>
              <td className={row.overdue ? 'text-danger font-weight-bold' : ''}>
                {row.due_at ? formatDay(row.due_at) : '—'}
              </td>
              {/* Frozen at issue time. Deliberately not totalled. */}
              <td className="text-right">{formatAmount(row.amount_due)}</td>
              <td style={{ whiteSpace: 'nowrap' }}>
                {this.statusBadge(row)}
                {row.emailed_at ? null : (
                  <Badge color="warning" className="ml-1">
                    Not emailed
                  </Badge>
                )}
              </td>
              {/* There is no admin-name resolver on this page and no lookup
                  endpoint to add, so the raw admin user id is shown. */}
              <td>{row.issued_by ? `#${row.issued_by}` : '—'}</td>
            </tr>
          ))}
        </tbody>
      </DataTable>
    );
  }

  render() {
    const { rows, meta, page, loaded, error } = this.state;
    const outstanding = Number(meta.outstanding_now) || 0;

    return (
      <Card className="mt-3">
        <CardHeader>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <strong>Payment notes</strong>
              {meta.total ? (
                <span className="text-muted"> — {meta.total} issued</span>
              ) : null}
            </div>
            {loaded && !error ? (
              <div className="text-right">
                <small className="text-muted d-block">Currently owed</small>
                <h5 className={`mb-0 ${outstanding > 0 ? 'text-danger' : ''}`}>
                  {formatAmount(outstanding)}
                </h5>
              </div>
            ) : null}
          </div>
        </CardHeader>
        <CardBody>
          {rows.length ? (
            <Alert color="secondary">
              Each row shows what was requested for that month, frozen at the
              moment it was issued. There is only one overdraft balance, so
              these amounts do not add up to a debt — the live figure is the{' '}
              <strong>{formatAmount(outstanding)}</strong> currently owed above.
            </Alert>
          ) : null}

          {this.renderBody()}

          {rows.length ? (
            <Pagination
              total={meta.pages || 1}
              page={page}
              onChange={this.setPage}
            />
          ) : null}
        </CardBody>
      </Card>
    );
  }
}

export default PaymentNoteHistory;
